import React, { useMemo } from 'react'

import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { useTable } from '~/common/hooks/useTable'
import Checkbox from '~/components/Checkbox'
import {
  DEFAULT_MIN_COLUMN_WIDTH,
  TableProvider,
} from '~/contexts/TableContext'
import { useClasses } from '~/themes'

import Pagination from './Pagination'
import TableBody from './TableBody'
import TableHead from './TableHead'
import TableRow from './TableRow'
import TopBar from './TopBar'
import Truncate from './Truncate'
import style from './style'

/**
 * Data Table
 */
const DataTable = () => {
  const { t } = useTranslation(['buseye'])
  const classes = useClasses(style)

  const {
    rows,
    visibleColumns,
    maxHeight,
    selected,
    onSelectionChange,
    rowSpanMatrix,
    rowGrayMatrix,
    containerRef,
    uniqKey,
    isTableResizable,
    checkboxSelection,
    reorderable,
    isVisible,
    highlights,
    selectSingle,
    striped,
    mergeSetting,
    rowClass
  } = useTable()

  /**
   * Handle select or deselect row
   * @param {*} indexValue
   * @returns
   */
  const handleSelectOrDeselectRow = (indexValue) => {
    if (!checkboxSelection) return
    const selectedIndex = selected.findIndex(
      (item) => item[uniqKey] === indexValue,
    )
    let newSelected = []

    const newValueData = rows.find((item) => item[uniqKey] === indexValue)

    if (selectSingle) {
      if (selected?.[0]?.[uniqKey] === indexValue) {
        newSelected = []
      } else {
        newSelected = [newValueData]
      }

      onSelectionChange(newSelected)
      return
    }

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, newValueData)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }

    onSelectionChange(newSelected)
  }

  /**
   * Check if row is selected
   * @param {*} uniqKeyValue
   * @returns
   */
  const isSelected = (uniqKeyValue) => {
    return selected.findIndex((item) => item[uniqKey] === uniqKeyValue) !== -1
  }

  const getColumnsInBottomTree = (cols = []) => {
    const childCols = cols?.reduce((acc, cur) => {
      if (
        Array.isArray(cur?.columns) &&
        cur?.columns?.some((c) => isVisible(c))
      ) {
        return [...acc, ...cur?.columns?.filter((c) => isVisible(c))]
      }
      return [...acc, cur]
    }, [])

    if (childCols?.some((x) => x?.columns)) {
      return getColumnsInBottomTree(childCols)
    }

    return childCols
  }

  const bodyColumns = useMemo(
    () => getColumnsInBottomTree(visibleColumns),
    [visibleColumns],
  )

  const hasStickyCol = useMemo(
    () => visibleColumns.some((c) => c?.sticky),
    [visibleColumns],
  )

  return (
    <>
      <TopBar />

      <TableContainer
        ref={containerRef}
        className={classes.tableContainer}
        sx={{
          // maxHeight: maxHeight || 'calc(100vh - 160px)',
          maxHeight: maxHeight,

          '.MuiDialog-container &': {
            maxHeight: maxHeight || 'calc(100vh - 280px)',
          },
        }}
      >
        <Table
          stickyHeader
          className={classes.table}
          sx={
            isTableResizable || hasStickyCol
              ? { tableLayout: 'fixed', width: '100%' }
              : {}
          }
        >
          <TableHead />

          <TableBody>
            {rows?.length > 0 &&
              rows.map((row, index) => {
                const isItemSelected = isSelected(row[uniqKey])
                                return (
                  <TableRow
                    key={row[uniqKey] || index}
                    draggableId={row[uniqKey]?.toString()}
                    index={index}
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    className={clsx(
                      classes.tableRow,
                      classes.tableRowBorderGrid,
                      {
                        [classes.tableRowStriped]: striped && !rowGrayMatrix,
                        // [classes.tableRowBorder]: !striped,
                        // [classes.tableRowHover]: hover,
                        [classes.tableRowGray]: rowGrayMatrix?.[index],
                      },
                      rowClass ? rowClass[index] : ''
                    )}
                    classes={classes}
                  >
                    {checkboxSelection && (
                      <TableCell
                        className={clsx(
                          classes.tableCell,
                          classes.tableCellCheckbox,
                          {
                            [classes.tableCellHighlight]: highlights?.includes(
                              row[uniqKey],
                            ),
                          },
                        )}
                        sx={{
                          position: 'sticky',
                          left: reorderable ? 50 : 0,
                          zIndex: 10,
                          width: '200px',
                        }}
                      >
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() =>
                            handleSelectOrDeselectRow(row[uniqKey])
                          }
                        />
                      </TableCell>
                    )}
                    {bodyColumns.map((column, i) => {
                      const {
                        field,
                        align,
                        renderCell,
                        width,
                        minWidth,
                        cellStyle = {},
                        sticky,
                      } = column
                      const cellValue = renderCell
                        ? renderCell({ row }, index)
                        : row[field]

                      let colWidth = width || minWidth
                      let colMinWidth =
                        minWidth || width || DEFAULT_MIN_COLUMN_WIDTH
                      let colStickyLeft =
                        (reorderable ? 50 : 0) + (checkboxSelection ? 50 : 0)
                      let colStickyRight = 0

                      for (let c of visibleColumns || []) {
                        if (c?.field === field) break

                        if (c?.sticky === 'left' && isVisible(c)) {
                          colStickyLeft +=
                            c?.width || c?.minWidth || DEFAULT_MIN_COLUMN_WIDTH
                        }
                      }

                      for (let c of [...(visibleColumns || [])].reverse()) {
                        if (c?.field === field) break

                        if (c?.sticky === 'right' && isVisible(c)) {
                          colStickyRight +=
                            c?.width || c?.minWidth || DEFAULT_MIN_COLUMN_WIDTH
                        }
                      }

                      const rowSpan = rowSpanMatrix?.[index]?.[i]

                      if (
                        rowSpan === -1 ||
                        (row[mergeSetting?.rowSpanKey] &&
                          mergeSetting?.removeKeys[
                            [row[mergeSetting?.rowSpanKey]]
                          ]?.includes(column.field))
                      )
                        return null // remove td

                      return (
                        <TableCell
                          className={clsx(classes.tableCell, {
                            [classes[`tableCellAlign${align}`]]: align,

                            [classes.firstStickyRight]:
                              sticky === 'right' &&
                              visibleColumns?.find((s) => s?.sticky === 'right')
                                ?.field === field,
                            [classes.tableCellHighlight]: highlights?.includes(
                              row[uniqKey],
                            ),
                          })}
                          key={`data-table-${field}-${i}`}
                          id={`data-table-${field}-${i}`}
                          sx={{
                            width: colWidth,
                            minWidth: colMinWidth,
                            verticalAlign: 'middle',
                            ...(sticky
                              ? {
                                  position: 'sticky',
                                  [sticky]:
                                    sticky === 'left'
                                      ? colStickyLeft
                                      : colStickyRight,
                                  zIndex: 10,
                                }
                              : {}),
                            ...cellStyle,
                          }}
                          {...(rowSpan > 1 ? { rowSpan } : {})}
                          {...(row[mergeSetting?.rowSpanKey] &&
                          Object.keys(
                            mergeSetting?.colSpan[
                              row[mergeSetting?.rowSpanKey]
                            ],
                          ).includes(column.field)
                            ? {
                                colSpan:
                                  mergeSetting?.colSpan[
                                    row[mergeSetting?.rowSpanKey]
                                  ][column.field],
                              }
                            : {})}
                        >
                          <Truncate value={cellValue} classes={classes} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}

            {!rows?.length && (
              <TableRow>
                <TableCell
                  className={classes.tableCell}
                  colSpan={visibleColumns.length + (checkboxSelection ? 1 : 0)}
                  sx={(theme) => ({
                    textAlign: 'center !important',
                    color: theme.palette.subText.main,
                  })}
                >
                  {t('dataTable.noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination />
    </>
  )
}

export default (props) => (
  <TableProvider {...props}>
    <DataTable />
  </TableProvider>
)
